import { Spin } from '@/ui/compat/antd'
import { useLocation } from 'react-router-dom'
import { useAccessControl } from '../context/AccessControlContext'
import NoPermissionsView from '../app/admin/modules/NoPermissionsView'
import { buildPermissionCode, matchInterfaceCodeForPath } from '../utils/accessControlUtils'

/**
 * Blocks route rendering when the user lacks the required permission(s).
 * - `permission`: exact code
 * - `interfaceCode` + `action`: single action
 * - `interfaceCode` + `actions`: any-of actions
 * - else: resolve interface from current path (`view`)
 */
const RequirePermission = ({
  children,
  permission,
  interfaceCode,
  action = 'view',
  actions,
}) => {
  const location = useLocation()
  const { can, canPath, loading, isSuperAdmin } = useAccessControl()

  if (loading && !isSuperAdmin) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Spin size="large" />
      </div>
    )
  }

  let allowed = false
  let requiredCode = permission

  if (permission) {
    allowed = can(permission)
  } else if (interfaceCode) {
    const actionList = Array.isArray(actions) && actions.length > 0
      ? actions
      : [action]
    requiredCode = actionList
      .map((actionCode) => buildPermissionCode(interfaceCode, actionCode))
      .join(' | ')
    allowed = actionList.some((actionCode) =>
      can(buildPermissionCode(interfaceCode, actionCode))
    )
  } else {
    allowed = canPath(location.pathname)
    const iface = matchInterfaceCodeForPath(location.pathname)
    if (iface) {
      requiredCode = buildPermissionCode(iface, 'view')
    }
  }

  if (!allowed) {
    return (
      <NoPermissionsView
        permissions={requiredCode ? [{ code: requiredCode, name: requiredCode }] : []}
      />
    )
  }

  return children
}

export default RequirePermission
